import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Input,
  Select,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorMode,
  useTheme,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faRotateRight, faSearch } from "@fortawesome/free-solid-svg-icons";
import AsyncSelect from "react-select/async";
import { components } from "react-select";
import debounce from "lodash.debounce";
import moment from "moment";
import { cqAPI, loadCustomers } from "../../lib/Auth";
import { useAppContext } from "../../AppContext";
import SupportOutboundComposer from "./SupportOutboundComposer";
import "./Support.css";

const SUPPORT_BADGE_TOPIC = "support_badge_update";
const PAGE_SIZE = 25;

// '' is the default "active" bucket on the API: everything except `ai`.
const STATUS_FILTERS = [
  { value: "", label: "Active" },
  { value: "ai", label: "AI handled" },
  { value: "open", label: "Open" },
  { value: "waiting_on_customer", label: "Waiting on customer" },
  { value: "closed", label: "Closed" },
  { value: "all", label: "All" },
  // SS-4211: threads WE started. Not a status: the API is asked for `source=outbound`,
  // whose own default bucket includes `ai` — where an outbound thread waits on the practice.
  { value: "outbound", label: "Outbound" },
];

const OUTBOUND_FILTER = "outbound";

/** The inbox query for a filter choice: `Outbound` is a source, everything else a status. */
export const inboxFilterQuery = (choice) => (choice === OUTBOUND_FILTER
  ? "status=&source=outbound"
  : `status=${encodeURIComponent(choice)}`);

const STATUS_BADGES = {
  ai: { label: "AI handled", colorScheme: "purple" },
  open: { label: "Open", colorScheme: "red" },
  waiting_on_customer: { label: "Waiting on customer", colorScheme: "yellow" },
  closed: { label: "Closed", colorScheme: "gray" },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_BADGES[status] || { label: status || "—", colorScheme: "gray" };
  return (
    <Badge colorScheme={meta.colorScheme} whiteSpace="nowrap">
      {meta.label}
    </Badge>
  );
};

// Pausing does not change the status, so a paused thread is indistinguishable from
// any other row without this. Subtle: it qualifies the status, it is not one.
const AiPausedBadge = () => (
  <Badge colorScheme="orange" variant="subtle" whiteSpace="nowrap">
    AI paused
  </Badge>
);

// SS-4211: a thread we started, and one where the practice accepted a change nobody has
// made yet.
const OutboundBadges = ({ ticket }) => (ticket.source === "outbound" ? (
  <>
    <Badge colorScheme="teal" variant="subtle" whiteSpace="nowrap">Outbound</Badge>
    {ticket.outbound?.action_pending && (
      <Badge colorScheme="red" variant="solid" whiteSpace="nowrap">Change to make</Badge>
    )}
  </>
) : null);

const DropdownIndicator = (props) =>
  components.DropdownIndicator && (
    <components.DropdownIndicator {...props}>
      <FontAwesomeIcon icon={faSearch} />
    </components.DropdownIndicator>
  );

const formatLocationOption = (option) => (
  <Flex direction="column" py={1}>
    <Text>{option.name}</Text>
  </Flex>
);

const readHash = () => new URLSearchParams(window.location.hash.slice(1));

function SupportInbox() {
  const hash = readHash();
  const navigate = useNavigate();
  const toast = useToast();
  const theme = useTheme();
  const { colorMode } = useColorMode();
  const { realtime, realtimeInitialized } = useAppContext();

  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  // The cap the server hit while searching message bodies, or 0 if it did not.
  const [searchTruncated, setSearchTruncated] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  // The newest read failed, so what is on screen belongs to a question nobody asked.
  // "Empty" and "we do not know" have to look different: the rows from the PREVIOUS
  // filter sat under the new selector with the spinner cleared, and once the toast went
  // away — three seconds — the page looked settled and wrong. On an empty previous
  // filter it went further and said the new one matched nothing, which is a claim about
  // a queue that was never loaded.
  const [loadFailed, setLoadFailed] = useState(false);
  const [status, setStatus] = useState(() => hash.get("status") || "");
  const [page, setPage] = useState(() => Number(hash.get("page")) || 1);
  const [query, setQuery] = useState(() => hash.get("q") || "");
  const [searchTerm, setSearchTerm] = useState(() => hash.get("q") || "");
  const [selectedLocation, setSelectedLocation] = useState(() => {
    const bid = hash.get("bid");
    return bid ? { _id: bid, name: hash.get("bidName") || bid } : null;
  });

  const bid = selectedLocation?._id?.toString() || "";
  const fetchRef = useRef(null);
  // Request ordering — see fetchTickets.
  const issuedRef = useRef(0);

  const hoverBgColor = colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];
  const unreadBgColor = colorMode === "light" ? theme.colors.blue[50] : theme.colors.gray[700];
  const mutedColor = colorMode === "light" ? "gray.600" : "gray.400";
  const cardBorder = colorMode === "light" ? "gray.200" : "gray.600";

  const fetchTickets = useCallback(async () => {
    setIsLoading(true);
    // Only the NEWEST request may paint. Typing in the search box, changing a filter and
    // the realtime badge all fire this, so several are routinely in flight — and they
    // need not come back in order.
    //
    // Measured against what has been ISSUED, not against what was last rendered. Ordering
    // by render was not enough: switch from Active to Closed while the Active read is
    // still running and it comes back first, so nothing newer has rendered yet and it
    // paints Active rows under a Closed selector — then clears the spinner, so the page
    // looks settled and wrong. If the Closed read then fails or hangs, it stays that way.
    // A superseded answer is never the answer to the question on screen.
    const at = (issuedRef.current += 1);
    const stale = () => at !== issuedRef.current;
    try {
      // bid= is always present so cqAPI doesn't graft the page bid onto the query.
      const response = await cqAPI.get(
        `/support/inbox?${inboxFilterQuery(status)}&bid=${encodeURIComponent(bid)}` +
          `&q=${encodeURIComponent(searchTerm)}&page=${page}&limit=${PAGE_SIZE}`
      );
      if (stale()) return;
      if (response?.type !== "ok") {
        setLoadFailed(true);
        setTickets([]);
        setTotal(0);
        setSearchTruncated(0);
        toast({
          title: "Error",
          description: response?.msg || "Failed to load support tickets.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      setLoadFailed(false);
      setTickets(response.data?.tickets || []);
      setTotal(response.data?.total || 0);
      // The server stopped counting matches partway through the archive. Said out loud:
      // a short list with no explanation reads as "that is all there is".
      setSearchTruncated(
        response.data?.truncated ? response.data.search_limit || 0 : 0
      );
    } catch (error) {
      if (stale()) return;
      setLoadFailed(true);
      setTickets([]);
      setTotal(0);
      setSearchTruncated(0);
      console.error("Error fetching support tickets:", error);
      toast({
        title: "Error",
        description: "Failed to load support tickets.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      if (!stale()) setIsLoading(false);
    }
  }, [status, bid, searchTerm, page, toast]);

  useEffect(() => {
    fetchRef.current = fetchTickets;
  }, [fetchTickets]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Keep the filters in the URL hash so a reload (or a shared link) restores them.
  useEffect(() => {
    const params = new URLSearchParams({
      status,
      bid,
      ...(selectedLocation?.name ? { bidName: selectedLocation.name } : {}),
      q: searchTerm,
      page: String(page),
    });
    window.location.hash = params.toString();
  }, [status, bid, selectedLocation, searchTerm, page]);

  useEffect(() => {
    const client = realtime.current;
    if (!client) return undefined;
    const handler = () => fetchRef.current && fetchRef.current();
    client.subscribeToTopic(SUPPORT_BADGE_TOPIC, null, handler);
    return () => client.unsubscribeFromTopic(SUPPORT_BADGE_TOPIC, null, handler);
  }, [realtimeInitialized, realtime]);

  const loadOptions = useCallback(
    debounce((inputValue, callback) => {
      loadCustomers(inputValue).then((options) => callback(options));
    }, 500),
    []
  );

  const applySearch = () => {
    setPage(1);
    setSearchTerm(query);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") applySearch();
  };

  const openTicket = (ticketId) => navigate(`/support/${ticketId}`);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  const lastActivity = (ticket) => {
    const at = ticket.last_message_at || ticket.created;
    if (!at) return "—";
    return (
      <Tooltip label={moment(at).format("LLL")} hasArrow>
        <Text as="span" whiteSpace="nowrap">
          {moment(at).fromNow()}
        </Text>
      </Tooltip>
    );
  };

  return (
    <Box className="support-page" p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
        <Text fontSize="xl" fontWeight="bold">
          Support
        </Text>
        <Flex gap={2}>
          <Button
            size="sm"
            colorScheme="blue"
            leftIcon={<FontAwesomeIcon icon={faPenToSquare} />}
            onClick={() => setComposerOpen(true)}
          >
            New message
          </Button>
          <Button
            size="sm"
            leftIcon={<FontAwesomeIcon icon={faRotateRight} />}
            onClick={fetchTickets}
            isLoading={isLoading}
          >
            Refresh
          </Button>
        </Flex>
      </Flex>

      <SupportOutboundComposer
        isOpen={composerOpen}
        onClose={() => setComposerOpen(false)}
        onSent={(result, practice) => {
          setComposerOpen(false);
          const opened = result?.tickets || [];
          // One thread: go to it. Several: show them together — the practice's outbound
          // threads, with any search that would hide them cleared.
          if (opened.length === 1) {
            openTicket(opened[0]._id);
            return;
          }
          setPage(1);
          setQuery("");
          setSearchTerm("");
          if (practice?._id) setSelectedLocation({ _id: practice._id, name: practice.name });
          setStatus(OUTBOUND_FILTER);
          // ...and read it again even when those filters were already showing: nothing
          // above changes then, and the badge push that would refresh it is best effort.
          fetchTickets();
        }}
      />

      <Flex mb={4} wrap="wrap" gap={2} align="center">
        <Select
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
          width={{ base: "100%", md: "200px" }}
        >
          {STATUS_FILTERS.map((option) => (
            <option key={option.value || "active"} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>

        <Box width={{ base: "100%", md: "220px" }}>
          <AsyncSelect
            loadOptions={loadOptions}
            value={selectedLocation}
            getOptionValue={(option) => option._id}
            formatOptionLabel={formatLocationOption}
            placeholder="All businesses"
            components={{ DropdownIndicator }}
            className="search-input"
            isClearable
            // .search-input carries a global width:50%, so pin the container open
            styles={{ container: (base) => ({ ...base, width: "100%", minWidth: "100%" }) }}
            onChange={(option) => {
              setPage(1);
              setSelectedLocation(option);
            }}
          />
        </Box>

        <Flex flex={{ base: "1 1 100%", md: "0 1 320px" }} gap={2}>
          {/* minW lets the INPUT absorb a squeeze; flexShrink pins the button so
              its label can never be crushed against its own border. */}
          <Input
            placeholder="Search subject or message"
            minW={0}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onSearchKeyDown}
          />
          <Button flexShrink={0} onClick={applySearch} leftIcon={<FontAwesomeIcon icon={faSearch} />}>
            Search
          </Button>
        </Flex>
      </Flex>

      {!!searchTruncated && !isLoading && (
        // Worded as a caveat about the SEARCH, not a claim about the results. The cap is
        // applied to message matches before the status filter and independently of the
        // subject match, so a capped search can still have found everything — the flag
        // only knows that it stopped looking early.
        <Text fontSize="sm" color={mutedColor} mb={3}>
          {`Message search stopped after ${searchTruncated} conversations mentioning "${searchTerm}", so there may be more. Narrow the search or pick a business.`}
        </Text>
      )}

      {isLoading ? (
        <Flex justify="center" align="center" py={10}>
          <Spinner color="blue.500" size="xl" />
        </Flex>
      ) : (
        <>
          {/* Desktop table */}
          <TableContainer
            display={{ base: "none", md: "block" }}
            borderWidth="1px"
            borderRadius="lg"
          >
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Status</Th>
                  <Th>Business</Th>
                  <Th>User</Th>
                  <Th>Subject</Th>
                  <Th>Last message</Th>
                  <Th>Last activity</Th>
                </Tr>
              </Thead>
              <Tbody>
                {tickets.length === 0 ? (
                  <Tr>
                    <Td colSpan={6}>
                      <Text fontSize="sm" color={mutedColor}>
                        {loadFailed
                          ? "These tickets could not be loaded. Try again."
                          : "No support tickets match these filters."}
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  tickets.map((ticket) => (
                    <Tr
                      key={ticket._id}
                      bg={ticket.unread ? unreadBgColor : undefined}
                      fontWeight={ticket.unread ? "bold" : "normal"}
                      _hover={{ bg: hoverBgColor, cursor: "pointer" }}
                      onClick={() => openTicket(ticket._id)}
                    >
                      <Td>
                        <Flex align="center" gap={2} wrap="wrap">
                          {ticket.unread && (
                            <Box w="8px" h="8px" borderRadius="full" bg="blue.400" flexShrink={0} />
                          )}
                          <StatusBadge status={ticket.status} />
                          {ticket.ai_paused && <AiPausedBadge />}
                          <OutboundBadges ticket={ticket} />
                        </Flex>
                      </Td>
                      <Td maxW="180px" overflow="hidden" textOverflow="ellipsis">
                        {ticket.business_name || "—"}
                      </Td>
                      <Td maxW="180px" overflow="hidden" textOverflow="ellipsis">
                        <Tooltip label={ticket.user_email} hasArrow isDisabled={!ticket.user_email}>
                          <Text as="span">{ticket.user_name || ticket.user_email || "—"}</Text>
                        </Tooltip>
                      </Td>
                      <Td maxW="260px" overflow="hidden" textOverflow="ellipsis">
                        {ticket.subject || "(no subject)"}
                      </Td>
                      <Td maxW="320px" overflow="hidden" textOverflow="ellipsis">
                        <Text as="span" fontWeight="normal" color={mutedColor} noOfLines={1}>
                          {ticket.last_message_preview || "—"}
                        </Text>
                      </Td>
                      <Td>{lastActivity(ticket)}</Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>

          {/* Mobile cards */}
          <Stack display={{ base: "flex", md: "none" }} spacing={3}>
            {tickets.length === 0 ? (
              <Text fontSize="sm" color={mutedColor}>
                {loadFailed
                  ? "These tickets could not be loaded. Try again."
                  : "No support tickets match these filters."}
              </Text>
            ) : (
              tickets.map((ticket) => (
                <Box
                  key={ticket._id}
                  borderWidth="1px"
                  borderColor={cardBorder}
                  borderRadius="lg"
                  bg={ticket.unread ? unreadBgColor : undefined}
                  p={3}
                  onClick={() => openTicket(ticket._id)}
                >
                  <Flex justify="space-between" align="center" gap={2} mb={1}>
                    <Flex align="center" gap={2} minW={0} wrap="wrap">
                      {ticket.unread && (
                        <Box w="8px" h="8px" borderRadius="full" bg="blue.400" flexShrink={0} />
                      )}
                      <StatusBadge status={ticket.status} />
                      {ticket.ai_paused && <AiPausedBadge />}
                      <OutboundBadges ticket={ticket} />
                    </Flex>
                    <Text fontSize="xs" color={mutedColor} whiteSpace="nowrap">
                      {moment(ticket.last_message_at || ticket.created).fromNow()}
                    </Text>
                  </Flex>
                  <Text fontWeight={ticket.unread ? "bold" : "semibold"} noOfLines={2} textAlign="left">
                    {ticket.subject || "(no subject)"}
                  </Text>
                  <Text fontSize="xs" color={mutedColor} noOfLines={1} textAlign="left">
                    {[ticket.business_name, ticket.user_name || ticket.user_email]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </Text>
                  <Text fontSize="sm" color={mutedColor} noOfLines={2} mt={1} textAlign="left">
                    {ticket.last_message_preview || "—"}
                  </Text>
                </Box>
              ))
            )}
          </Stack>

          <Flex mt={4} align="center" justify="space-between" wrap="wrap" gap={2}>
            <Text fontSize="sm" color={mutedColor}>
              {total === 0 ? "No tickets" : `Showing ${rangeStart}–${rangeEnd} of ${total}`}
            </Text>
            <Flex align="center" gap={2}>
              <Button size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} isDisabled={page <= 1}>
                Previous
              </Button>
              <Text fontSize="sm">
                Page {page} of {pageCount}
              </Text>
              <Button
                size="sm"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                isDisabled={page >= pageCount}
              >
                Next
              </Button>
            </Flex>
          </Flex>
        </>
      )}
    </Box>
  );
}

export default SupportInbox;
